<script setup lang="ts">
import { Dialog, Button } from 'primevue';
import { computed, ref, watch } from 'vue';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import type { ActionResponse, SuccessResponse } from '../api/httpClient';
import type { Translations } from 'shared/translations/domain/translations';

defineExpose({
  show
});

export interface DeleteEntity {
  id: string;
  name: string;
}

export interface DeleteDialogTranslations {
  header: (name: string) => string;
  description: string;
  deleteButton: string;
}

const emit = defineEmits<{
  deleted: [id: string];
}>();

const props = defineProps<{
  delete: (id: string) => Promise<ActionResponse<SuccessResponse>>;
  getById: (id: string) => Promise<DeleteEntity>;
  translate: (translations: Translations) => DeleteDialogTranslations;
}>();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const dialogTranslations = computed(() => props.translate(translations.value));

const entityId = ref<string>();
const entityName = ref<string>('');
const visible = ref(false);
const loading = ref(false);
const problems = ref<ProblemDetails>();

watch(entityId, (newProductId) => {
  if (!newProductId) {
    visible.value = false;
  }
});

async function show(id: string) {
  try {
    problems.value = undefined;
    loading.value = true;
    const entity = await props.getById(id);

    entityId.value = entity.id;
    entityName.value = entity.name;

    visible.value = true;
  } finally {
    loading.value = false;
  }
}

async function executeDelete() {
  const id = entityId.value;
  if (!id) throw new Error();
  try {
    loading.value = true;
    const response = await props.delete(id);

    if (response.ok) {
      emit('deleted', id);
      entityId.value = undefined;
      visible.value = false;
    } else {
      problems.value = response.details;
    }

  } finally {
    loading.value = false;
  }
}

function onHide() {
  entityId.value = undefined;
  visible.value = false;
}

</script>

<template>
  <Dialog modal @hide="onHide" :header="dialogTranslations.header(entityName)" v-model:visible="visible">
    <ProblemDetailsView v-model:model-value="problems"></ProblemDetailsView>
    <div class="flex items-center gap-4">
      <i class="pi pi-exclamation-triangle !text-3xl" style="color: var(--p-red-400)"></i>
      <span>{{ dialogTranslations.description }}</span>
    </div>
    <template #footer>
      <Button :label="dialogTranslations.deleteButton" :loading="loading" icon="pi pi-check"
        @click="executeDelete"></Button>
      <Button :label="translations.generic.cancel" icon="pi pi-times" text @click="onHide"></Button>
    </template>
  </Dialog>
</template>
